import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { SignupForm } from '../../../types';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const SignupPage: React.FC = () => {
  const auth = useAuth();
  const [formData, setFormData] = useState<SignupForm>({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    
    try {
      // Redirecionar para Cognito para criar conta
      await auth.signinRedirect({
        extraQueryParams: formData.email ? {
          login_hint: formData.email
        } : undefined
      });
    } catch (error) {
      console.error('Erro ao redirecionar para cadastro:', error);
      setErrors({ email: 'Erro ao redirecionar. Tente novamente.' });
      setIsLoading(false);
    }
  };

  const handleLoginClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 flex items-center justify-center py-8">
      <div className="grid md:grid-cols-2 gap-16 max-w-5xl w-full mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-12 border border-gray-200">
          <div className="text-center mb-8">
            <h1 className="text-blue-700 text-3xl font-bold mb-2">SinalInvest</h1>
            <h2 className="text-gray-900 text-2xl font-semibold mb-4">Criar minha conta</h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Comece em segundos. Cadastre-se e comece a receber insights valiosos sobre suas ações!
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mb-8">
            <div className="mb-4">
              <label htmlFor="name" className="block mb-2 font-medium text-gray-900">
                Nome completo
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Digite seu nome completo"
              />
              {errors.name && (
                <span className="text-red-500 text-sm mt-1">{errors.name}</span>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="email" className="block mb-2 font-medium text-gray-900">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 ${errors.email ? 'border-red-500' : ''}`}
                placeholder="Digite seu email"
              />
              {errors.email && (
                <span className="text-red-500 text-sm mt-1">{errors.email}</span>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block mb-2 font-medium text-gray-900">
                Senha
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 ${errors.password ? 'border-red-500' : ''}`}
                placeholder="Digite sua senha"
              />
              {errors.password && (
                <span className="text-red-500 text-sm mt-1">{errors.password}</span>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="block mb-2 font-medium text-gray-900">
                Confirmação de senha
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                placeholder="Confirme sua senha"
              />
              {errors.confirmPassword && (
                <span className="text-red-500 text-sm mt-1">{errors.confirmPassword}</span>
              )}
            </div>

            <button
              type="submit"
              className={`w-full mt-4 relative overflow-hidden flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-lg text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200 ${isLoading ? 'pointer-events-none' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin mr-2"></div>
                  Criando conta...
                </>
              ) : (
                'Criar minha conta'
              )}
            </button>
          </form>

          <div className="text-center">
            <p className="text-gray-600 mb-6">
              Já tem uma conta? <button type="button" className="text-blue-700 no-underline font-medium bg-transparent border-none p-0 cursor-pointer text-base hover:underline" onClick={handleLoginClick}>Fazer login</button>
            </p>
            
            <div className="flex items-center gap-3 p-4 bg-slate-100 rounded-lg border border-gray-200">
              <div className="text-xl flex-shrink-0">🔒</div>
              <p className="text-gray-600 text-sm leading-snug m-0">Seus dados estão seguros. Usamos criptografia de ponta para proteger suas informações.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-6 animate-bounce">📈</div>
            <h3 className="text-gray-900 text-3xl font-semibold mb-4">Transforme notícias em lucro</h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">Nossa IA analisa milhares de notícias e identifica oportunidades de investimento em tempo real.</p>
            
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-200 transition-transform duration-200 hover:translate-x-1 hover:shadow-md">
                <span className="text-xl w-6 text-center">⚡</span>
                <span className="text-gray-900 font-medium">Alertas instantâneos</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-200 transition-transform duration-200 hover:translate-x-1 hover:shadow-md">
                <span className="text-xl w-6 text-center">🎯</span>
                <span className="text-gray-900 font-medium">Análise personalizada</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-200 transition-transform duration-200 hover:translate-x-1 hover:shadow-md">
                <span className="text-xl w-6 text-center">🤖</span>
                <span className="text-gray-900 font-medium">IA avançada</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;

